import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, Check, CreditCard, Store, Download, Calendar, Clock, IndianRupee } from "lucide-react";
import { api, formatApiError, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function nextDays(n = 7) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    out.push(d);
  }
  return out;
}
const fmtDate = (d) => d.toISOString().slice(0, 10);

export default function Booking() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [doctor, setDoctor] = useState(null);
  const [date, setDate] = useState(fmtDate(new Date()));
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(null);
  const [step, setStep] = useState(1);
  const [patient, setPatient] = useState({ name: user?.name || "", age: "", phone: user?.phone || "", for_family_member_id: "" });
  const [payment, setPayment] = useState("pay_at_clinic");
  const [family, setFamily] = useState([]);
  const [booking, setBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const days = useMemo(() => nextDays(7), []);

  useEffect(() => {
    api.get(`/doctors/${id}`).then((r) => setDoctor(r.data));
    api.get("/patient/me").then((r) => setFamily(r.data?.family_members || []));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setTime(null);
    api.get(`/doctors/${id}/slots`, { params: { date } }).then((r) => setSlots(r.data));
  }, [id, date]);

  const confirm = async () => {
    if (!time) return toast.error("Pick a time slot");
    if (!patient.name) return toast.error("Patient name is required");
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments", {
        doctor_id: id,
        date,
        time,
        mode: "in_clinic",
        payment_method: payment,
        patient_name: patient.name,
        patient_age: patient.age ? Number(patient.age) : undefined,
        patient_phone: patient.phone,
        for_family_member_id: patient.for_family_member_id || undefined,
      });
      setBooking(data);
      setStep(3);
      toast.success("Appointment booked!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!doctor) return <div className="p-20 text-center text-slate-500">Loading…</div>;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-center gap-2 text-sm text-slate-500">
        <Link to="/doctors" className="hover:text-blue-700">Doctors</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/doctor/${id}`} className="hover:text-blue-700">{doctor.name}</Link>
        <ChevronRight className="h-4 w-4" />
        <span className="font-semibold text-slate-900">Book Appointment</span>
      </div>

      {/* Stepper */}
      <div className="mb-6 flex items-center gap-2">
        {[1, 2, 3].map((n) => (
          <div key={n} className="flex items-center gap-2">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
              step >= n ? "bg-blue-700 text-white" : "bg-slate-200 text-slate-600"
            }`}>{step > n ? <Check className="h-4 w-4" /> : n}</div>
            {n < 3 && <div className={`h-0.5 w-10 ${step > n ? "bg-blue-700" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      {step === 3 && booking ? (
        <div className="card p-8 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <Check className="h-8 w-8" />
          </div>
          <h2 className="mt-5 text-2xl font-extrabold text-slate-900">Appointment Confirmed</h2>
          <p className="mt-1 text-sm text-slate-600">We've booked your slot. See you at the clinic!</p>
          <div className="mx-auto mt-6 max-w-md rounded-2xl border-2 border-dashed border-blue-300 bg-blue-50 p-6 text-left">
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-700">Booking ID</div>
            <div className="mt-1 font-mono text-xl font-bold tracking-widest text-blue-900">#{booking.booking_id}</div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-slate-500">Doctor</div>
                <div className="font-semibold text-slate-900">{booking.doctor_name}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Specialty</div>
                <div className="font-semibold text-slate-900">{booking.doctor_specialty}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Date & Time</div>
                <div className="font-semibold text-slate-900">{booking.date} • {booking.time}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Fee</div>
                <div className="font-semibold text-slate-900">{formatINR(booking.fee)} • {booking.payment_method === "pay_at_clinic" ? "Pay at Clinic" : "Online"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-xs text-slate-500">Clinic</div>
                <div className="font-semibold text-slate-900">{booking.clinic_name}</div>
                <div className="text-xs text-slate-600">{booking.clinic_address}</div>
              </div>
            </div>
          </div>
          <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
            <button onClick={() => window.print()} className="btn-outline"><Download className="h-4 w-4" /> Download Pass</button>
            <button onClick={() => nav("/patient/dashboard")} data-testid="go-dashboard-btn" className="btn-primary">Go to Dashboard</button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
          <div className="space-y-6">
            <div className="card p-6">
              <h2 className="text-lg font-bold text-slate-900">Select Date & Time</h2>
              <div className="mt-5 flex gap-2 overflow-x-auto pb-2">
                {days.map((d) => {
                  const key = fmtDate(d);
                  const active = key === date;
                  return (
                    <button
                      key={key}
                      data-testid={`date-${key}`}
                      onClick={() => setDate(key)}
                      className={`shrink-0 rounded-xl border px-4 py-3 text-center transition-all ${
                        active ? "border-blue-700 bg-blue-700 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"
                      }`}
                    >
                      <div className="text-[11px] font-semibold uppercase tracking-wider">
                        {d.toLocaleDateString("en-IN", { weekday: "short" })}
                      </div>
                      <div className="mt-0.5 text-lg font-extrabold">{d.getDate()}</div>
                      <div className="text-[11px]">{d.toLocaleDateString("en-IN", { month: "short" })}</div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-5">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Available Slots</div>
                {slots.length === 0 ? (
                  <div className="mt-3 text-sm text-slate-500">No slots on this date.</div>
                ) : (
                  <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
                    {slots.map((s) => {
                      const selected = time === s.time;
                      return (
                        <button
                          key={s.time}
                          data-testid={`slot-${s.time}`}
                          disabled={!s.available}
                          onClick={() => setTime(s.time)}
                          className={`slot-pill ${
                            !s.available ? "slot-pill-taken" : selected ? "slot-pill-selected" : "slot-pill-available"
                          }`}
                        >
                          {s.time}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="card p-6">
              <h2 className="text-lg font-bold text-slate-900">Patient Details</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <select
                  data-testid="booking-for-select"
                  className="input-field sm:col-span-2"
                  value={patient.for_family_member_id}
                  onChange={(e) => {
                    const fm = family.find((f) => f.id === e.target.value);
                    setPatient({
                      ...patient,
                      for_family_member_id: e.target.value,
                      name: fm?.name || user?.name || "",
                      age: fm?.age || "",
                      phone: fm?.phone || user?.phone || "",
                    });
                  }}
                >
                  <option value="">Myself ({user?.name})</option>
                  {family.map((f) => (
                    <option key={f.id} value={f.id}>{f.name} ({f.relation})</option>
                  ))}
                </select>
                <input data-testid="booking-name" className="input-field" placeholder="Full name" value={patient.name} onChange={(e) => setPatient({ ...patient, name: e.target.value })} />
                <input data-testid="booking-age" className="input-field" placeholder="Age" type="number" value={patient.age} onChange={(e) => setPatient({ ...patient, age: e.target.value })} />
                <input data-testid="booking-phone" className="input-field sm:col-span-2" placeholder="+91 phone number" value={patient.phone} onChange={(e) => setPatient({ ...patient, phone: e.target.value })} />
              </div>
            </div>

            <div className="card p-6">
              <h2 className="text-lg font-bold text-slate-900">Payment Method</h2>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  data-testid="pay-at-clinic-radio"
                  onClick={() => setPayment("pay_at_clinic")}
                  className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition-all ${
                    payment === "pay_at_clinic" ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <Store className="mt-0.5 h-5 w-5 text-emerald-700" />
                  <div>
                    <div className="font-bold text-slate-900">Pay at Clinic</div>
                    <div className="mt-0.5 text-xs text-slate-600">Pay in cash or UPI directly at the clinic reception.</div>
                  </div>
                </button>
                <button
                  data-testid="pay-online-radio"
                  onClick={() => setPayment("online")}
                  className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition-all ${
                    payment === "online" ? "border-blue-500 bg-blue-50 ring-2 ring-blue-500/20" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <CreditCard className="mt-0.5 h-5 w-5 text-blue-700" />
                  <div>
                    <div className="font-bold text-slate-900">Pay Online (Demo)</div>
                    <div className="mt-0.5 text-xs text-slate-600">Instant confirmation. Payment gateway coming soon.</div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <aside>
            <div className="card sticky top-24 p-6">
              <div className="flex items-center gap-3">
                <img src={doctor.photo_url} alt={doctor.name} className="h-14 w-14 rounded-xl object-cover" />
                <div>
                  <div className="font-bold text-slate-900">{doctor.name}</div>
                  <div className="text-xs text-blue-700">{doctor.specialty}</div>
                </div>
              </div>
              <div className="mt-5 space-y-3 border-t border-slate-100 pt-5 text-sm">
                <div className="flex items-start gap-2 text-slate-700">
                  <Calendar className="mt-0.5 h-4 w-4 text-slate-400" />
                  <span>{date}{time ? ` • ${time}` : ""}</span>
                </div>
                <div className="flex items-start gap-2 text-slate-700">
                  <Clock className="mt-0.5 h-4 w-4 text-slate-400" />
                  <span>{doctor.slot_duration_min} min consultation</span>
                </div>
                <div className="flex items-start gap-2 text-slate-700">
                  <IndianRupee className="mt-0.5 h-4 w-4 text-slate-400" />
                  <span>Consultation Fee <strong className="text-slate-900">{formatINR(doctor.consultation_fee)}</strong></span>
                </div>
              </div>
              <button data-testid="confirm-booking-btn" disabled={submitting || !time} onClick={confirm} className="btn-primary mt-6 w-full">
                {submitting ? "Booking…" : "Confirm Booking"}
              </button>
              <p className="mt-3 text-center text-[11px] text-slate-500">By confirming you agree to our terms & cancellation policy.</p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
